import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  Business,
  People,
  TrendingUp,
  AttachMoney
} from '@mui/icons-material';

import customerService from '../../services/customerService';
import { Customer, CustomerStats, CustomerStatus } from '../../types/customer';
import { formatXOF } from '../../utils/currency';

interface CustomersListState {
  customers: Customer[];
  stats: CustomerStats | null;
  loading: boolean;
  statsLoading: boolean;
  error: string | null;
  searchTerm: string;
  deletingId: number | null;
}

const CustomersList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [state, setState] = useState<CustomersListState>({
    customers: [],
    stats: null,
    loading: true,
    statsLoading: true,
    error: null,
    searchTerm: '',
    deletingId: null,
  });

  // Fetch customers data
  const fetchCustomers = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const customers = await customerService.getCustomers();
      // Ensure we always have an array, even if service returns null/undefined
      setState(prev => ({ ...prev, customers: Array.isArray(customers) ? customers : [], loading: false }));
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      const errorMessage = error.response?.data?.message || error.message || t('customers.messages.loadError');
      // Set empty array on error to prevent null reference issues
      setState(prev => ({ ...prev, customers: [], error: errorMessage, loading: false }));
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [t, enqueueSnackbar]);

  // Fetch customer statistics
  const fetchStats = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, statsLoading: true }));
      const stats = await customerService.getCustomerStats();
      setState(prev => ({ ...prev, stats, statsLoading: false }));
    } catch (error: any) {
      console.error('Error fetching customer stats:', error);
      setState(prev => ({ ...prev, statsLoading: false }));
      // Don't show error for stats as it's not critical
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [fetchCustomers, fetchStats]);

  // Handle customer deletion
  const handleDeleteCustomer = async (customer: Customer) => {
    if (!window.confirm(t('customers.messages.confirmDelete', { name: customer.company_name }))) {
      return;
    }

    try {
      setState(prev => ({ ...prev, deletingId: customer.user_id }));
      await customerService.deleteCustomer(customer.user_id);
      // Remove customer from state
      setState(prev => ({
        ...prev,
        customers: prev.customers.filter(c => c.user_id !== customer.user_id),
        deletingId: null
      }));
      enqueueSnackbar(t('customers.messages.deleted'), { variant: 'success' });
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      const errorMessage = error.response?.data?.message || error.message || t('customers.messages.deleteError');
      setState(prev => ({ ...prev, deletingId: null }));
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchCustomers();
    fetchStats();
  };

  // Filter customers based on search term - ensure customers is always an array
  const filteredCustomers = (state.customers || []).filter(customer =>
    customer.company_name?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    customer.contact_person?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
    customer.phone?.includes(state.searchTerm)
  );

  // Get status color
  const getStatusColor = (status: CustomerStatus): 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'warning';
    }
  };

  // Format currency for Burkina Faso (XOF)
  const formatCurrency = (amount: number): string => {
    return formatXOF(amount);
  };

  // Render statistics cards
  const renderStatsCards = () => {
    if (state.statsLoading) {
      return (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card>
                <CardContent>
                  <CircularProgress size={24} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (!state.stats) return null;

    const stats = [
      {
        title: t('customers.stats.totalCustomers'),
        value: state.stats.total_customers,
        icon: <People />,
        color: theme.palette.primary.main
      },
      {
        title: t('customers.stats.activeCustomers'),
        value: state.stats.active_customers,
        icon: <TrendingUp />,
        color: theme.palette.success.main
      },
      {
        title: t('customers.stats.totalRevenue'),
        value: formatCurrency(state.stats.total_revenue),
        icon: <AttachMoney />,
        color: theme.palette.info.main
      },
      {
        title: t('customers.stats.averageOrderValue'),
        value: formatCurrency(state.stats.average_order_value),
        icon: <Business />,
        color: theme.palette.warning.main
      }
    ];

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h6">
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (state.loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (state.error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={handleRefresh}
              startIcon={<Refresh />}
            >
              {t('common.retry')}
            </Button>
          }
        >
          <AlertTitle>{t('customers.messages.error')}</AlertTitle>
          {state.error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('customers.title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate('/customers/create')}
        >
          {t('customers.createNew')}
        </Button>
      </Box>

      {/* Statistics Cards */}
      {renderStatsCards()}

      {/* Search and Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <TextField
          placeholder={t('customers.searchPlaceholder')}
          value={state.searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Customers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('customers.fields.companyName')}</TableCell>
              <TableCell>{t('customers.fields.contactPerson')}</TableCell>
              <TableCell>{t('customers.fields.email')}</TableCell>
              <TableCell>{t('customers.fields.phone')}</TableCell>
              <TableCell>{t('customers.fields.status')}</TableCell>
              <TableCell align="center">{t('customers.fields.totalShipments')}</TableCell>
              <TableCell align="center">{t('customers.fields.totalSpent')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {state.searchTerm ? t('customers.noSearchResults') : t('customers.noCustomers')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow key={customer.user_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {customer.company_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{customer.contact_person}</TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`customers.status.${customer.status}`)}
                      color={getStatusColor(customer.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{customer.total_shipments}</TableCell>
                  <TableCell align="center">{formatCurrency(customer.total_spent)}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title={t('common.view')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/customers/${customer.user_id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/customers/${customer.user_id}/edit`)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={state.deletingId === customer.user_id}
                          onClick={() => handleDeleteCustomer(customer)}
                        >
                          {state.deletingId === customer.user_id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default CustomersList;
